import { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/Common";
import { FileText, UploadCloud, Star, Trash2, Eye, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const KEYWORDS = ["Lebenslauf", "Anschreiben", "Deutsch B1/B2", "Küchenerfahrung", "HACCP", "Kost & Logis"];

export default function CVManager() {
  const [cvs, setCvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  const load = async () => {
    const res = await api.get("/cv");
    setCvs(res.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Solo file PDF"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/cv/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("CV caricato con successo", { description: file.name });
      load();
    } catch (e) { toast.error("Upload fallito", { description: e?.response?.data?.detail }); }
    finally { setUploading(false); }
  };

  const setPrimary = async (id) => { await api.post(`/cv/${id}/set-primary`); toast.success("CV principale aggiornato"); load(); };
  const del = async (id) => { await api.delete(`/cv/${id}`); toast.success("CV eliminato"); load(); };

  return (
    <div className="p-6 max-w-[1200px]">
      <PageHeader title="Gestione CV & Documenti" subtitle="Carica il tuo CV in PDF: verrà allegato automaticamente alle candidature" icon={FileText} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div
            data-testid="cv-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`card p-10 flex flex-col items-center justify-center cursor-pointer text-center transition-all ${drag ? "border-amber-500/60 bg-amber-500/5" : ""}`}
          >
            <input ref={inputRef} type="file" accept="application/pdf" hidden data-testid="cv-file-input"
              onChange={(e) => upload(e.target.files[0])} />
            <div className="w-14 h-14 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-3">
              {uploading ? <Spinner className="w-6 h-6" /> : <UploadCloud className="w-7 h-7 text-amber-400" />}
            </div>
            <div className="font-head font-semibold text-gray-100">Trascina qui il tuo CV (PDF)</div>
            <div className="text-sm text-gray-500 mt-1">oppure clicca per selezionare · max 8MB</div>
          </div>

          <div className="card overflow-hidden">
            {loading ? (
              <div className="py-12 flex justify-center"><Spinner className="w-6 h-6" /></div>
            ) : cvs.length === 0 ? (
              <EmptyState icon={FileText} title="Nessun CV caricato" hint="Carica il tuo Lebenslauf in PDF per iniziare a candidarti." />
            ) : (
              <div className="divide-y divide-white/5">
                {cvs.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-4" data-testid={`cv-item-${c.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-100 truncate">{c.filename}</span>
                        {c.is_primary && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Principale</span>}
                      </div>
                      <div className="text-xs text-gray-500">{(c.size / 1024).toFixed(0)} KB · {new Date(c.uploaded_at).toLocaleString("it-IT")}</div>
                    </div>
                    <a href={`${API}/cv/${c.id}/preview`} target="_blank" rel="noreferrer" data-testid={`cv-preview-${c.id}`}
                      className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-amber-400 transition-colors"><Eye className="w-4 h-4" /></a>
                    {!c.is_primary && <button data-testid={`cv-primary-${c.id}`} onClick={() => setPrimary(c.id)}
                      className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-emerald-400 transition-colors" title="Imposta principale"><Star className="w-4 h-4" /></button>}
                    <button data-testid={`cv-delete-${c.id}`} onClick={() => del(c.id)}
                      className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card p-5 h-fit">
          <h3 className="font-head font-semibold text-gray-200 mb-3">Suggerimenti per la Germania</h3>
          <p className="text-sm text-gray-400 mb-4">Includi queste parole chiave nel CV per il settore Gastronomie tedesco:</p>
          <div className="flex flex-wrap gap-2">
            {KEYWORDS.map((k) => (
              <span key={k} className="text-xs px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30">{k}</span>
            ))}
          </div>
          <div className="mt-5 text-xs text-gray-500 leading-relaxed border-t border-white/10 pt-4">
            Un buon <span className="text-gray-300">Lebenslauf</span> (CV) in tedesco aumenta il tasso di risposta degli hotel. Menziona il tuo livello di tedesco e l'esperienza in cucina.
          </div>
        </div>
      </div>
    </div>
  );
}
